import { TFunction } from "i18next";
import { range, sumBy } from "lodash";

import { ChartDisplayType } from "glue/Chart/chart.constants";
import { CrremPathway } from "graphql-types/graphql";
import {
    ReductionAnalysisChartData,
    ReductionProposal,
} from "legacyNext/types";
import { formatNumberLocale, gramsToKg } from "utils/report.helpers";

export const getFormattedChartData = (
    area: number,
    type: ChartDisplayType,
    enabledProposals: ReductionProposal[],
    data: Omit<ReductionAnalysisChartData, "red">[]
) => {
    let reduction = 0;
    return data.map((intensity) => {
        const proposal = enabledProposals.find(
            (proposal) => proposal.startYear === intensity.date.getFullYear()
        );
        if (proposal) {
            const saving =
                type === ChartDisplayType.EMISSIONS
                    ? gramsToKg(proposal.emissionSaving)
                    : proposal.energySaving;

            reduction += saving / area;
        }

        const { int, proj, date } = intensity;
        const red = proj ? Math.max(proj - reduction, 0) : null;

        return {
            ...intensity,
            int: int
                ? proj && proj > int
                    ? proj
                    : int
                : date.getFullYear() > new Date().getFullYear()
                ? null
                : proj,
            red: date.getFullYear() >= new Date().getFullYear() ? red : null,
            proj: date.getFullYear() >= new Date().getFullYear() ? proj : null,
            diff:
                proj && (int || date.getFullYear() === new Date().getFullYear())
                    ? Math.abs(proj - (int || 0))
                    : null,
        };
    });
};

export const getReductionDetails = (
    t: TFunction,
    pathways: Omit<CrremPathway, "country" | "version" | "target">[],
    enabledProposals: ReductionProposal[],
    chartData: ReductionAnalysisChartData[]
) => {
    const format = (number: number, max: number) =>
        number ? formatNumberLocale(number, t, { max }) : null;

    const strandedYear = range(2025, 2050).find((year) => {
        const value = chartData.find(
            (item) => item.date.getFullYear() === year
        );
        if (!value) return false;

        const crremValues = pathways.filter((pathway) => pathway.year === year);

        const { proj } = value;

        return crremValues.some(
            (crremValue) => proj !== null && proj > crremValue.value
        );
    });

    const reductionStrandedYear =
        range(2025, 2050).find((year) => {
            const value = chartData.find(
                (item) => item.date.getFullYear() === year
            );
            if (!value) return false;

            const crremValues = pathways.filter(
                (pathway) => pathway.year === year
            );

            const { red } = value;
            return crremValues.some(
                (crremValue) => red !== null && red > crremValue.value
            );
        }) ?? 2050;

    return [
        {
            id: "savings",
            label: t("legacyNext.calculatedSavings", "calculated savings"),
            value: format(sumBy(enabledProposals, "costSaving"), 0),
            unit: "kr. / år",
        },
        {
            id: "investment",
            label: t("legacyNext.investment", "investment"),
            value: format(
                sumBy(enabledProposals, (p) => Number(p.investment)),
                0
            ),
            unit: "kr.",
        },
        {
            id: "co2Reduction",
            label: t("legacyNext.co2reduction", "co2 reduction"),
            value: format(
                gramsToKg(sumBy(enabledProposals, "emissionSaving")),
                2
            ),
            unit: "kg CO2e / år",
        },
        {
            id: "energyReduction",
            label: t("legacyNext.energySaving", "energy saving"),
            value: format(sumBy(enabledProposals, "energySaving"), 2),
            unit: "kWh / år",
        },
        {
            id: "strandingYear",
            label: t("legacyNext.strandingYear", "stranding year"),
            value: strandedYear
                ? `${strandedYear} → ${reductionStrandedYear || "-"}`
                : null,
            unit: t("legacyNext.moveNYears", "moved {{ count }} years", {
                count: reductionStrandedYear - (strandedYear ?? 0),
            }),
        },
    ];
};
